/**
 * questionarios-store — interface com /api/v1/admin/questionario-templates
 * + /api/v1/service-orders/:osId/questionario/respostas (FEAT-V2-008, Bloco B).
 *
 * Substitui o legacy `useChecklistTemplates` em `service-orders-store.ts`
 * (que foi removido junto com os endpoints `/api/v1/checklist-templates`).
 *
 * React Query hooks:
 * - useQuestionarioTemplates({ workflowId?, includeDeprecated? }) — lista
 * - useQuestionarioTemplate(id) — single
 * - useQuestionarioPerguntas(templateId) — lista perguntas
 * - useQuestionarioRespostas(osId) — lista respostas de uma OS
 * - useQuestionarioActions() — create/update/delete (templates + perguntas)
 * - useSubmitRespostas() — POST resposta do técnico
 */

import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

// ─── Tipos públicos ─────────────────────────────────────────────────────

export const TIPOS_RESPOSTA = [
  'sim_nao',
  'texto_curto',
  'texto_longo',
  'numero',
  'multipla_escolha',
  'unica_escolha',
  'rating_1_5',
  'data_hora',
  'foto',
  'assinatura_inline',
  'anexo',
] as const;

export type TipoResposta = (typeof TIPOS_RESPOSTA)[number];

export interface QuestionarioTemplate {
  id: string;
  // Vinculação flexível (Bloco F.1): EXATAMENTE 1 dos 2 não-null
  workflowId: string | null;
  activityTypeId: string | null;
  /** Shape do activity_type joined no backend (Bloco F.1). NULL quando
   *  template vinculado por workflow, ou quando activity_type foi removido. */
  activityType: { id: string; slug: string; label: string } | null;
  nome: string;
  descricao: string | null;
  ativo: boolean;
  versao: number;
  templatePaiId: string | null;
  ativoEmIso: string | null;
  deprecatedEmIso: string | null;
  criadoEmIso: string;
}

export interface QuestionarioPergunta {
  id: string;
  templateId: string;
  ordem: number;
  codigo: string;
  pergunta: string;
  tipoResposta: TipoResposta;
  opcoes: string[];
  obrigatoria: boolean;
  isPublic: boolean;
  condicionalPerguntaCodigo: string | null;
  condicionalValor: unknown;
  acoes: unknown[];
  criadoEmIso: string;
}

export interface QuestionarioResposta {
  id: string;
  osId: string;
  templateId: string;
  respostas: Record<string, unknown>;
  preenchidoPorId: string | null;
  preenchidoEmIso: string;
}

export interface CreateTemplateInput {
  // EXATAMENTE 1 dos 2 (Bloco F.1 — XOR enforced no backend)
  workflowId?: string | null;
  activityTypeId?: string | null;
  nome: string;
  descricao?: string | null;
  ativo?: boolean;
}

export type UpdateTemplateInput = Partial<CreateTemplateInput>;

export interface CreatePerguntaInput {
  codigo: string;
  pergunta: string;
  tipoResposta: TipoResposta;
  opcoes?: string[];
  ordem?: number;
  obrigatoria?: boolean;
  isPublic?: boolean;
  condicionalPerguntaCodigo?: string | null;
  condicionalValor?: unknown;
  acoes?: unknown[];
}

export type UpdatePerguntaInput = Partial<CreatePerguntaInput>;

// ─── Keys ────────────────────────────────────────────────────────────────

export const QUESTIONARIO_KEYS = {
  templates: (workflowId: string | null, includeDeprecated: boolean) =>
    ['v1', 'questionario-templates', { workflowId, includeDeprecated }] as const,
  template: (id: string) => ['v1', 'questionario-templates', id] as const,
  perguntas: (templateId: string) =>
    ['v1', 'questionario-templates', templateId, 'perguntas'] as const,
  respostas: (osId: string) =>
    ['v1', 'service-orders', osId, 'questionario-respostas'] as const,
};

// ─── Queries ─────────────────────────────────────────────────────────────

interface UseTemplatesOptions {
  workflowId?: string | null;
  includeDeprecated?: boolean;
  /** BF.4: incluir templates arquivados (`ativo=0`). Default false. */
  includeArchived?: boolean;
  enabled?: boolean;
}

export function useQuestionarioTemplates(options: UseTemplatesOptions = {}) {
  const {
    workflowId = null,
    includeDeprecated = false,
    includeArchived = false,
    enabled: enabledOpt = true,
  } = options;
  const params = new URLSearchParams();
  if (workflowId) params.set('workflow_id', workflowId);
  if (includeDeprecated) params.set('include_deprecated', 'true');
  if (includeArchived) params.set('include_inactive', 'true');
  const qs = params.toString();
  const url = qs
    ? `/api/v1/admin/questionario-templates?${qs}`
    : '/api/v1/admin/questionario-templates';

  return useQuery({
    queryKey: [
      ...QUESTIONARIO_KEYS.templates(workflowId, includeDeprecated),
      { includeArchived },
    ] as const,
    queryFn: () => api.get<QuestionarioTemplate[]>(url),
    enabled: enabledOpt && hasToken(),
    staleTime: 60_000,
  });
}

export function useQuestionarioTemplate(id: string | null) {
  return useQuery({
    queryKey: QUESTIONARIO_KEYS.template(id ?? ''),
    queryFn: () => api.get<QuestionarioTemplate>(`/api/v1/admin/questionario-templates/${encodeURIComponent(id!)}`),
    enabled: !!id && hasToken(),
    staleTime: 60_000,
  });
}

export function useQuestionarioPerguntas(templateId: string | null) {
  return useQuery({
    queryKey: QUESTIONARIO_KEYS.perguntas(templateId ?? ''),
    queryFn: () =>
      api.get<QuestionarioPergunta[]>(
        `/api/v1/admin/questionario-templates/${encodeURIComponent(templateId!)}/perguntas`
      ),
    enabled: !!templateId && hasToken(),
    staleTime: 60_000,
  });
}

export function useQuestionarioRespostas(osId: string | null) {
  return useQuery({
    queryKey: QUESTIONARIO_KEYS.respostas(osId ?? ''),
    queryFn: () =>
      api.get<QuestionarioResposta[]>(
        `/api/v1/service-orders/${encodeURIComponent(osId!)}/questionario/respostas`
      ),
    enabled: !!osId && hasToken(),
    staleTime: 30_000,
  });
}

// ─── Mutations (CRUD admin + submit técnico) ────────────────────────────

export function useQuestionarioActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidateAll = useCallback(
    () => qc.invalidateQueries({ queryKey: ['v1', 'questionario-templates'] }),
    [qc]
  );

  const createTemplate = useCallback(
    async (input: CreateTemplateInput): Promise<QuestionarioTemplate> => {
      setPending('create-template');
      try {
        const t = await api.post<QuestionarioTemplate>(
          '/api/v1/admin/questionario-templates',
          input
        );
        invalidateAll();
        return t;
      } finally {
        setPending(null);
      }
    },
    [invalidateAll]
  );

  const updateTemplate = useCallback(
    async (id: string, input: UpdateTemplateInput): Promise<QuestionarioTemplate> => {
      setPending(id);
      try {
        const t = await api.put<QuestionarioTemplate>(
          `/api/v1/admin/questionario-templates/${encodeURIComponent(id)}`,
          input
        );
        invalidateAll();
        return t;
      } finally {
        setPending(null);
      }
    },
    [invalidateAll]
  );

  const deleteTemplate = useCallback(
    async (id: string): Promise<void> => {
      setPending(id);
      try {
        await api.delete(`/api/v1/admin/questionario-templates/${encodeURIComponent(id)}`);
        invalidateAll();
      } finally {
        setPending(null);
      }
    },
    [invalidateAll]
  );

  const createPergunta = useCallback(
    async (
      templateId: string,
      input: CreatePerguntaInput
    ): Promise<QuestionarioPergunta & { versionedTemplateId: string | null }> => {
      setPending(`p-create-${templateId}`);
      try {
        const p = await api.post<QuestionarioPergunta & { versionedTemplateId: string | null }>(
          `/api/v1/admin/questionario-templates/${encodeURIComponent(templateId)}/perguntas`,
          input
        );
        invalidateAll();
        return p;
      } finally {
        setPending(null);
      }
    },
    [invalidateAll]
  );

  const updatePergunta = useCallback(
    async (
      id: string,
      input: UpdatePerguntaInput
    ): Promise<QuestionarioPergunta & { versionedTemplateId: string | null }> => {
      setPending(id);
      try {
        const p = await api.put<QuestionarioPergunta & { versionedTemplateId: string | null }>(
          `/api/v1/admin/questionario-perguntas/${encodeURIComponent(id)}`,
          input
        );
        invalidateAll();
        return p;
      } finally {
        setPending(null);
      }
    },
    [invalidateAll]
  );

  const deletePergunta = useCallback(
    async (id: string): Promise<{ versionedTemplateId: string | null }> => {
      setPending(id);
      try {
        const r = await api.delete<{ versionedTemplateId: string | null }>(
          `/api/v1/admin/questionario-perguntas/${encodeURIComponent(id)}`
        );
        invalidateAll();
        return r;
      } finally {
        setPending(null);
      }
    },
    [invalidateAll]
  );

  return useMemo(
    () => ({
      createTemplate,
      updateTemplate,
      deleteTemplate,
      createPergunta,
      updatePergunta,
      deletePergunta,
      pending,
    }),
    [createTemplate, updateTemplate, deleteTemplate, createPergunta, updatePergunta, deletePergunta, pending]
  );
}

export function useSubmitRespostas() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const submit = useCallback(
    async (osId: string, templateId: string, respostas: Record<string, unknown>) => {
      setPending(osId);
      try {
        const r = await api.post<QuestionarioResposta>(
          `/api/v1/service-orders/${encodeURIComponent(osId)}/questionario/respostas`,
          { templateId, respostas }
        );
        qc.invalidateQueries({ queryKey: QUESTIONARIO_KEYS.respostas(osId) });
        return r;
      } finally {
        setPending(null);
      }
    },
    [qc]
  );

  return { submit, pending };
}
